/**
 * SystemMessageBanner
 *
 * Fetches active Semprion HQ announcements from
 * `/api/system-messages/active` (Phase D) and shows dismissible banners
 * at the top of the Dashboard. Dismissals are per-session (localStorage)
 * so HQ can re-show a critical message simply by toggling active=false
 * and re-creating it.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Info, AlertCircle, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISSED_KEY = 'ol.sysmsg.dismissed';

const PALETTE = {
  info:     { bg: 'bg-sky-500/10',    border: 'border-sky-500/40',    text: 'text-sky-300',    Icon: Info },
  warning:  { bg: 'bg-amber-500/10',  border: 'border-amber-500/40',  text: 'text-amber-300',  Icon: AlertTriangle },
  critical: { bg: 'bg-red-500/10',    border: 'border-red-500/40',    text: 'text-red-300',    Icon: AlertCircle },
};

export default function SystemMessageBanner() {
  const [msgs, setMsgs] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); } catch { return []; }
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/system-messages/active`)
      .then(({ data }) => setMsgs(data.messages || []))
      .catch(() => {});
  }, []);

  const dismiss = useCallback((id) => {
    const next = Array.from(new Set([...dismissed, id]));
    setDismissed(next);
    try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(next)); } catch {}
  }, [dismissed]);

  const visible = msgs.filter(m => !dismissed.includes(m.id));
  if (visible.length === 0) return null;

  return (
    <div className="w-full px-4 lg:px-6 pt-2 space-y-2" data-testid="system-message-banner">
      {visible.map(m => {
        const p = PALETTE[m.severity] || PALETTE.info;
        const Icon = p.Icon;
        return (
          <div key={m.id} className={`flex items-start gap-3 border rounded-md px-3 py-2 ${p.bg} ${p.border} ${p.text}`}
               data-testid={`sysmsg-${m.id}`}>
            <Icon className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{m.title}</div>
              {m.body && <div className="text-xs opacity-90 mt-0.5">{m.body}</div>}
            </div>
            <button onClick={() => dismiss(m.id)} className="opacity-60 hover:opacity-100" data-testid={`sysmsg-dismiss-${m.id}`}>
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
